/**
 * GuiderPage - Content hub for guides and articles
 *
 * SEO Target: /arsredovisning/guider
 * Primary keywords: årsredovisning guider, bokslut hjälp, årsredovisning tips
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

interface Guide {
  title: string;
  description: string;
  href: string;
  category: string;
  readTime: string;
  isNew?: boolean;
}

const guides: Guide[] = [
  {
    title: "Din första årsredovisning - En komplett guide",
    description:
      "Steg-för-steg guide för dig som ska göra årsredovisning för första gången. Vi går igenom allt från bokslut till inlämning.",
    href: "/arsredovisning/guider/forsta-arsredovisningen",
    category: "Nybörjare",
    readTime: "15 min",
    isNew: true,
  },
  {
    title: "K2 vs K3 - Vilket regelverk ska du välja?",
    description:
      "Komplett jämförelse mellan K2 och K3. Vi hjälper dig avgöra vilket regelverk som passar ditt företag bäst.",
    href: "/arsredovisning/guider/k2-vs-k3",
    category: "Regelverk",
    readTime: "10 min",
  },
  {
    title: "SIE-fil guide - Allt du behöver veta",
    description:
      "Vad är en SIE-fil? Hur exporterar du den från Fortnox, Visma eller Spiris? Vilken SIE-typ behöver du?",
    href: "/arsredovisning/guider/sie-fil",
    category: "Praktiskt",
    readTime: "8 min",
  },
  {
    title: "Nya K2-regler från 2026",
    description:
      "Viktiga förändringar i K2 som träder i kraft 2026. BRF, kryptotillgångar och utländska filialer påverkas.",
    href: "/arsredovisning/guider/nya-regler-2026",
    category: "Regelverk",
    readTime: "7 min",
    isNew: true,
  },
  {
    title: "Noter i årsredovisningen - Komplett översikt",
    description:
      "Vilka noter krävs enligt K2? Vi går igenom alla obligatoriska och villkorade noter med exempel.",
    href: "/arsredovisning/guider/noter",
    category: "Praktiskt",
    readTime: "12 min",
  },
  {
    title: "Förvaltningsberättelse - Mall och tips",
    description:
      "Så skriver du förvaltningsberättelsen. Med mall för verksamhetsbeskrivning, flerårsöversikt och resultatdisposition.",
    href: "/arsredovisning/guider/forvaltningsberattelse",
    category: "Praktiskt",
    readTime: "10 min",
  },
];

const categories = ["Alla", "Nybörjare", "Regelverk", "Praktiskt"];

export function GuiderPage() {
  const [selectedCategory, setSelectedCategory] = useState("Alla");

  const filteredGuides = selectedCategory === "Alla"
    ? guides
    : guides.filter((guide) => guide.category === selectedCategory);

  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
  ]);

  return (
    <>
      <PageMeta
        title="Guider & artiklar om årsredovisning | Kvanten"
        description="Kostnadsfria guider om årsredovisning, bokslut och K2/K3-regelverk. Lär dig allt du behöver veta för att göra din årsredovisning själv."
        keywords="årsredovisning guide, bokslut hjälp, K2 guide, K3 guide, SIE-fil guide, noter årsredovisning, förvaltningsberättelse"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Guider & artiklar
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Kostnadsfria guider om årsredovisning, bokslut och regelverken K2 och K3.
              Skrivet av experter, anpassat för dig som vill göra din egen årsredovisning.
            </p>
          </div>
        </div>
      </section>

      {/* Guides list */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  cat === selectedCategory
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Guides grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGuides.map((guide) => (
              <Link
                key={guide.href}
                to={guide.href}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    {guide.category}
                  </span>
                  {guide.isNew && (
                    <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded">
                      Ny
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{guide.readTime} läsning</span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {guide.title}
                </h2>
                <p className="text-gray-600 text-sm mb-4">{guide.description}</p>
                <span className="inline-flex items-center text-blue-600 font-medium text-sm group-hover:text-blue-700">
                  Läs guide
                  <ArrowRightIcon />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Redo att skapa din årsredovisning?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Nu när du har läst på är det dags att sätta igång. Ladda upp din SIE-fil och kom
            igång gratis.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/wizard"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Prova direkt
              <ArrowRightIcon />
            </Link>
            <Link
              to="/arsredovisning"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Läs mer om produkten
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
